<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, useAttrs } from 'vue'

const props = withDefaults(defineProps<Props>(), {
  label: '',
  id: '',
  modelValue: 0,
  error: false,
  errorMessage: '',
  wrapperClass: '',
  autoFocus: false,
  allowNegative: true,
})

interface Props {
  label?: string
  id?: string
  modelValue?: number
  error?: boolean
  errorMessage?: string
  wrapperClass?: string
  autoFocus?: boolean
  allowNegative?: boolean
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
  (e: 'blur', ev: FocusEvent): void
  (e: 'focus', ev: FocusEvent): void
}>()

function generateId(): string {
  if (
    typeof globalThis !== 'undefined' &&
    'crypto' in globalThis &&
    globalThis.crypto?.randomUUID
  ) {
    return globalThis.crypto.randomUUID()
  }

  return `input-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const inputId = ref<string>(props.id || generateId())

const model = computed<number>({
  get: () => props.modelValue ?? 0,
  set: (val) => emit('update:modelValue', val),
})

const attrs = useAttrs()

const describedBy = computed(() => {
  return props.error && props.errorMessage ? `${inputId.value}-feedback` : undefined
})

const spinnerInput = ref<HTMLInputElement | null>(null)

function incrementValue(add: boolean): void {
  const current = Number.isFinite(Number(model.value)) ? Number(model.value) : 0

  let nextValue = add ? current + 1 : current - 1

  if (!props.allowNegative) {
    if (nextValue < 0) {
      nextValue = 0
    }
  }

  emit('update:modelValue', Number(nextValue))
}

function onFocus(e: FocusEvent) {
  const input = e.target as HTMLInputElement
  input.select()
  emit('focus', e)
}

function onBlur(e: FocusEvent) {
  if (!props.allowNegative) {
    if (model.value < 0) {
      model.value = 0
    }
  }
  emit('blur', e)
}

onMounted(async () => {
  if (!props.autoFocus) return

  await nextTick()

  if (!spinnerInput.value) return

  spinnerInput.value.focus()

  requestAnimationFrame(() => {
    spinnerInput.value.select()
  })
})
</script>

<template>
  <div :class="wrapperClass">
    <label v-if="props.label" :for="inputId" class="visually-hidden">
      {{ props.label }}
    </label>

    <div class="spinner mb-16">
      <button
        class="spinner-button btn btn-primary"
        :disabled="!props.allowNegative && model === 0"
        @click="incrementValue(false)"
      >
        -
      </button>
      <input
        v-bind="attrs"
        :id="inputId"
        ref="spinnerInput"
        v-model="model"
        type="number"
        :class="{ 'is-invalid': props.error }"
        :aria-invalid="props.error ? 'true' : 'false'"
        :aria-describedby="describedBy"
        @blur="onBlur"
        @focus="onFocus"
      />
      <button class="spinner-button btn btn-primary" @click="incrementValue(true)">+</button>
    </div>

    <div class="d-flex justify-content-center align-items-center flex-wrap gap-32">
      <slot name="extraButton" />
    </div>

    <div
      v-if="props.error && props.errorMessage"
      :id="`${inputId}-feedback`"
      class="invalid-feedback d-block mt-16"
    >
      {{ props.errorMessage }}
    </div>
  </div>
</template>
